/**
 * Årshjul for artikkelproduksjon — admin-UI.
 *
 * Erstatter det hardkodede MONTHLY_THEMES-rasteret: innholdsplanen redigeres her
 * i stedet for i kildekoden, og forstår merkedager (fast dato, n-te ukedag,
 * påske-offset) i stedet for bare «den 1. og 15.».
 *
 * Datologikken speiler functions/lib/articleWheel.js via utils/articleWheelDates.ts.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  CalendarDays, ChevronDown, Loader2, Play, RotateCcw, Sparkles, Mail, Zap, Check,
} from 'lucide-react';
import type { ArticleWheelConfig, ArticleWheelEntry } from '../../../types';
import {
  subscribeToArticleWheel, saveArticleWheel, seedArticleWheel, runArticleWheelNow,
} from '../../../services/articleService';
import {
  todayYmd, formatWheelDate, formatRelativeDays, describeRecurrence,
  computeRunwayMonths, buildWheelView,
} from '../../../utils/articleWheelDates';
import { Button } from '../../ui/Button';

const AUDIENCES: ArticleWheelEntry['targetAudience'][] = ['alle', 'bobil', 'campervan', 'taktelt', 'camp-let'];

interface Props {
  /**
   * Når satt viser panelet KUN denne merkedagen, utvidet. Årsstripa er da lista,
   * og panelet er detaljen — ellers ville de to komponentene vist de samme 20
   * merkedagene ved siden av hverandre, som var tilfellet før sammenslåingen.
   */
  selectedId?: string | null;
}

const AdminArticleWheelPanel: React.FC<Props> = ({ selectedId }) => {
  const [wheel, setWheel] = useState<ArticleWheelConfig | null>(null);
  const merged = selectedId !== undefined;
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState<'seed' | 'toggle' | 'dryrun' | 'save' | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => subscribeToArticleWheel(setWheel), []);

  const today = todayYmd();
  const entries = wheel?.entries ?? [];
  const hasEntries = entries.length > 0;

  const view = useMemo(() => buildWheelView(entries, today), [entries, today]);
  const activeCount = entries.filter((e) => e.enabled).length;
  const runway = computeRunwayMonths(wheel?.planHorizonYmd, today);
  const next = view.find((v) => v.entry.enabled);

  // Skillet mellom «passert i år» og «kommende» — «du er her» i planen.
  const firstUpcomingIdx = view.findIndex((v) => v.daysUntil >= 0 && v.entry.enabled);

  const isOpen = merged ? true : expanded;
  const visible = merged ? view.filter((v) => v.entry.id === selectedId) : view;

  const flash = (text: string) => {
    setMsg(text);
    setTimeout(() => setMsg(null), 5000);
  };

  const patchEntry = async (id: string, patch: Partial<ArticleWheelEntry>) => {
    if (!wheel) return;
    setBusy('save');
    try {
      await saveArticleWheel({ entries: entries.map((e) => (e.id === id ? { ...e, ...patch } : e)) });
    } catch (e) {
      flash(`Feil: ${e instanceof Error ? e.message : 'kunne ikke lagre'}`);
    } finally {
      setBusy(null);
    }
  };

  const handleToggleWheel = async () => {
    if (!wheel) return;
    setBusy('toggle');
    try {
      await saveArticleWheel({ enabled: !wheel.enabled });
      flash(wheel.enabled
        ? 'Årshjulet er av. Den gamle 1./15.-jobben overtar igjen.'
        : 'Årshjulet er på. Den gamle 1./15.-jobben står over.');
    } catch (e) {
      flash(`Feil: ${e instanceof Error ? e.message : 'kunne ikke lagre'}`);
    } finally {
      setBusy(null);
    }
  };

  const handleSeed = async () => {
    setBusy('seed');
    try {
      const res = await seedArticleWheel();
      flash(`Standardhjulet lastet: ${res.entries} merkedager. Hjulet er fortsatt ${res.enabled ? 'på' : 'AV'} — skru det på når du har sett gjennom planen.`);
    } catch (e) {
      flash(`Feil: ${e instanceof Error ? e.message : 'kunne ikke laste standardhjul'}`);
    } finally {
      setBusy(null);
    }
  };

  const handleDryRun = async () => {
    setBusy('dryrun');
    try {
      const res = await runArticleWheelNow(true);
      if (res.skipped === 'disabled') flash('Hjulet er av — ingenting ville blitt produsert.');
      else if (res.skipped === 'no-entries') flash('Ingen merkedager i hjulet ennå.');
      else if (res.skipped === 'nothing-due') flash('Ingen merkedager er forfalt i dag.');
      else {
        const names = (res.wouldCreate || []).map((w) => w.title).join(', ');
        const deferred = res.deferred?.length ? ` (${res.deferred.length} skjøvet til i morgen)` : '';
        flash(`Ville produsert: ${names || 'ingenting'}${deferred}.`);
      }
    } catch (e) {
      flash(`Feil: ${e instanceof Error ? e.message : 'testkjøring feilet'}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="bg-[var(--bg-surface)] border border-[var(--border-primary)] p-4 md:p-6 rounded-panel space-y-4">
      <div className="flex items-start justify-between gap-3">
        <button
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex items-center gap-3 text-emerald-400 min-w-0 text-left"
        >
          <CalendarDays size={20} className="flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-sm font-bold text-[var(--text-primary)]">Årshjul</h3>
            <p className="text-xs font-bold opacity-70">
              {!wheel ? 'Laster…'
                : !hasEntries ? 'Ingen merkedager ennå'
                : `${activeCount} av ${entries.length} aktive${next ? ` · neste: ${next.entry.title} ${formatRelativeDays(next.daysUntil)}` : ''}`}
            </p>
          </div>
          <ChevronDown size={16} className={`flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>

        {hasEntries && (
          <Button
            variant={wheel?.enabled ? 'primary' : 'secondary'}
            size="sm"
            className="flex-shrink-0"
            onClick={handleToggleWheel}
            disabled={busy !== null}
            loading={busy === 'toggle'}
            icon={<Zap size={12} />}
            aria-pressed={wheel?.enabled}
          >
            {wheel?.enabled ? 'På' : 'Av'}
          </Button>
        )}
      </div>

      {msg && (
        <div className={`text-xs font-bold px-4 py-2 rounded-card ${msg.startsWith('Feil') ? 'bg-red-500/10 text-red-400' : 'bg-emerald-500/10 text-emerald-400'}`}>
          {msg}
        </div>
      )}

      {!isOpen ? (
        <p className="text-xs text-[var(--text-muted)] font-bold leading-relaxed">
          {hasEntries
            ? `Merkedagsdrevet innholdsplan. Dekket til ${wheel?.planHorizonYmd ? formatWheelDate(wheel.planHorizonYmd) : 'årsslutt'} (~${Math.round(runway)} mnd). Utvid for å redigere.`
            : 'Erstatter det faste «1. og 15.»-rasteret med merkedager. Utvid for å komme i gang.'}
        </p>
      ) : (
        <>
          <p className="text-xs text-[var(--text-muted)] font-bold leading-relaxed">
            Cronen kjører 07:00 hver dag og produserer ÉN artikkel om gangen, for merkedager
            som er forfalt. Blir en oppføring nedprioritert, prøver den igjen neste dag —
            vinduet er selvhelende. Er hjulet på, står den gamle 1./15.-jobben over.
          </p>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              size="md"
              onClick={handleSeed}
              disabled={busy !== null}
              loading={busy === 'seed'}
              icon={hasEntries ? <RotateCcw size={14} /> : <Sparkles size={14} />}
            >
              {hasEntries ? 'Tilbakestill til standard' : 'Last standardhjul'}
            </Button>
            <Button
              variant="secondary"
              size="md"
              onClick={handleDryRun}
              disabled={busy !== null || !hasEntries}
              loading={busy === 'dryrun'}
              icon={<Play size={14} />}
            >
              Test uten å skrive
            </Button>
          </div>

          {merged && !selectedId && (
            <p className="text-xs text-[var(--text-muted)] py-8 text-center">
              Velg en merkedag i årshjulet til venstre for å redigere den.
            </p>
          )}

          {hasEntries && (
            <div className="space-y-2">
              {visible.map((v, idx) => {
                const e = v.entry;
                const isEditing = merged ? true : editingId === e.id;
                return (
                  <React.Fragment key={e.id}>
                    {!merged && idx === firstUpcomingIdx && idx > 0 && (
                      <div className="flex items-center gap-2 pt-2 pb-1">
                        <div className="h-px flex-1 bg-emerald-500/30" />
                        <span className="text-4xs font-black uppercase tracking-widest text-emerald-400">
                          Du er her · {formatWheelDate(today)}
                        </span>
                        <div className="h-px flex-1 bg-emerald-500/30" />
                      </div>
                    )}

                    <div className={`rounded-card border p-3 space-y-2 transition-all ${
                      v.windowOpen
                        ? 'bg-emerald-500/5 border-emerald-500/30'
                        : e.enabled
                          ? 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)]'
                          : 'bg-[var(--bg-surface-alt)] border-[var(--border-primary)] opacity-50'
                    }`}>
                      <div className="flex items-start justify-between gap-2">
                        <button onClick={() => setEditingId(isEditing ? null : e.id)} className="min-w-0 text-left flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-sm font-bold text-[var(--text-primary)] truncate">{e.title}</p>
                            {v.windowOpen && (
                              <span className="text-4xs font-black uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">
                                Forfalt nå
                              </span>
                            )}
                            {e.distributeViaEmail && (
                              <span title="Sender e-post til hele brukerbasen ved publisering">
                                <Mail size={11} className="text-amber-400" />
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-[var(--text-muted)] font-bold mt-0.5">
                            {e.when || describeRecurrence(e.recurrence)} · {formatWheelDate(v.nextYmd)}
                            {e.enabled && ` · ${formatRelativeDays(v.daysUntil)}`}
                          </p>
                          <p className="text-tiny text-[var(--text-muted)] mt-0.5">
                            Skrives {formatWheelDate(v.produceYmd)} ({e.leadDays} d før)
                            {e.autoReview ? ' · hurtiggodkjenning' : ' · stille utkast'}
                          </p>
                        </button>

                        {/* Ikon-toggle i tett liste: DESIGN.md tillater 24px AA-gulvet
                            der 44px bryter geometrien. Button (36px, pill) ville
                            sprengt radhøyden, så denne står hand-rullet med vilje. */}
                        <button
                          type="button"
                          onClick={() => patchEntry(e.id, { enabled: !e.enabled })}
                          disabled={busy !== null}
                          aria-label={e.enabled ? 'Deaktiver merkedag' : 'Aktiver merkedag'}
                          aria-pressed={e.enabled}
                          className={`p-2 rounded-pill border transition flex-shrink-0 disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 ${
                            e.enabled
                              ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                              : 'bg-transparent border-[var(--border-secondary)] text-[var(--text-muted)]'
                          }`}
                        >
                          <Check size={12} />
                        </button>
                      </div>

                      {isEditing && (
                        <div className="space-y-2 pt-2 border-t border-[var(--border-primary)]">
                          <label className="block">
                            <span className="text-4xs font-black uppercase tracking-widest text-[var(--text-muted)]">Tema</span>
                            <textarea
                              defaultValue={e.theme}
                              onBlur={(ev) => ev.target.value !== e.theme && patchEntry(e.id, { theme: ev.target.value })}
                              rows={2}
                              className="w-full mt-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card p-2 text-xs text-[var(--text-secondary)] resize-none focus:outline-none focus:border-emerald-500/50"
                            />
                          </label>

                          <label className="block">
                            <span className="text-4xs font-black uppercase tracking-widest text-[var(--text-muted)]">Vinkling (valgfri)</span>
                            <textarea
                              defaultValue={e.angle || ''}
                              onBlur={(ev) => ev.target.value !== (e.angle || '') && patchEntry(e.id, { angle: ev.target.value })}
                              rows={2}
                              placeholder="f.eks. «skriv for nybegynnere» eller «fokuser på budsjett»"
                              className="w-full mt-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card p-2 text-xs text-[var(--text-secondary)] resize-none focus:outline-none focus:border-emerald-500/50 placeholder:text-[var(--text-muted)]"
                            />
                          </label>

                          <label className="block">
                            <span className="text-4xs font-black uppercase tracking-widest text-[var(--text-muted)]">Notat</span>
                            <textarea
                              defaultValue={e.note || ''}
                              onBlur={(ev) => ev.target.value !== (e.note || '') && patchEntry(e.id, { note: ev.target.value })}
                              rows={2}
                              placeholder="Til deg selv — f.eks. «husk bomringen i år»"
                              className="w-full mt-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card p-2 text-xs text-[var(--text-secondary)] resize-none focus:outline-none focus:border-emerald-500/50 placeholder:text-[var(--text-muted)]"
                            />
                          </label>

                          <div className="grid grid-cols-2 gap-2">
                            <label className="block">
                              <span className="text-4xs font-black uppercase tracking-widest text-[var(--text-muted)]">Dager før</span>
                              <input
                                type="number" min={0} max={60} defaultValue={e.leadDays}
                                onBlur={(ev) => {
                                  const n = Math.min(60, Math.max(0, Number(ev.target.value) || 0));
                                  if (n !== e.leadDays) patchEntry(e.id, { leadDays: n });
                                }}
                                className="w-full mt-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card p-2 text-xs text-[var(--text-secondary)] focus:outline-none focus:border-emerald-500/50"
                              />
                            </label>
                            <label className="block">
                              <span className="text-4xs font-black uppercase tracking-widest text-[var(--text-muted)]">Målgruppe</span>
                              <select
                                defaultValue={e.targetAudience || 'alle'}
                                onChange={(ev) => patchEntry(e.id, { targetAudience: ev.target.value as ArticleWheelEntry['targetAudience'] })}
                                className="w-full mt-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card p-2 text-xs text-[var(--text-secondary)] focus:outline-none focus:border-emerald-500/50"
                              >
                                {AUDIENCES.map((a) => <option key={a} value={a}>{a}</option>)}
                              </select>
                            </label>
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            <label className="block">
                              <span className="text-4xs font-black uppercase tracking-widest text-[var(--text-muted)]">Bildestil</span>
                              <select
                                defaultValue={e.imageStyle || 'morsom'}
                                onChange={(ev) => patchEntry(e.id, { imageStyle: ev.target.value as 'morsom' | 'nostalgisk' })}
                                className="w-full mt-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-card p-2 text-xs text-[var(--text-secondary)] focus:outline-none focus:border-emerald-500/50"
                              >
                                <option value="morsom">Morsom karikatur</option>
                                <option value="nostalgisk">Nostalgisk plakat</option>
                              </select>
                            </label>
                            <div className="flex flex-col justify-end gap-1.5">
                              <Button
                                variant={e.autoReview ? 'primary' : 'secondary'}
                                size="sm"
                                fullWidth
                                onClick={() => patchEntry(e.id, { autoReview: !e.autoReview })}
                                disabled={busy !== null}
                                aria-pressed={e.autoReview}
                              >
                                {e.autoReview ? 'Hurtiggodkjenning' : 'Stille utkast'}
                              </Button>
                              {/* Amber bærer mening her (masseutsendelse), og Button har
                                  ingen warn-variant — så denne blir med vilje hand-rullet.
                                  Se docs/plan-admin-design-konvergens.md. */}
                              <button
                                type="button"
                                onClick={() => patchEntry(e.id, { distributeViaEmail: !e.distributeViaEmail })}
                                disabled={busy !== null}
                                aria-pressed={e.distributeViaEmail}
                                className={`w-full min-h-[36px] px-3 rounded-pill border text-tiny font-black uppercase tracking-widest transition disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 ${
                                  e.distributeViaEmail
                                    ? 'bg-amber-500/20 border-amber-500/40 text-amber-400'
                                    : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)] text-[var(--text-muted)]'
                                }`}
                              >
                                {e.distributeViaEmail ? 'E-post: PÅ' : 'E-post: av'}
                              </button>
                            </div>
                          </div>

                          {e.distributeViaEmail && (
                            <p className="text-tiny font-bold text-amber-400 bg-amber-500/10 rounded-card px-2 py-1.5">
                              Publisering av denne sender e-post til alle med varsler på.
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminArticleWheelPanel;
